import * as fuzz from 'fuzzball';

export interface FieldToMap {
  key: string;
  label: string;
}

export interface AutoMapOptions {
  threshold?: number;
  extraAliases?: Record<string, string[]>;
  conflictGroups?: Record<string, string[]>;
}

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const ALIASES: Record<string, string[]> = {
  client_name: ['name', 'recipient', 'customer', 'consignee', 'clientname', 'fullname', 'to', 'billto', 'buyername', 'partyname'],
  client_email: ['email', 'emailaddress', 'mail', 'e mail'],
  client_phone: ['phone', 'mobile', 'cell', 'contact', 'phonenumber', 'tel', 'telephone', 'contactnumber', 'whatsapp'],
  client_address: ['address', 'shippingaddress', 'deliveryaddress', 'addr', 'billingaddress', 'street', 'streetaddress'],
  company_name: ['company', 'organisation', 'organization', 'business', 'firm', 'vendor', 'supplier'],
  city: ['city', 'town'],
  state: ['state', 'province', 'region', 'county'],
  postal_code: ['zip', 'zipcode', 'postcode', 'postalcode', 'pincode', 'pin'],
  country: ['country', 'nation'],
  qty: ['quantity', 'qty', 'units', 'count', 'noofunits'],
  rate: ['rate', 'unitprice', 'unit price', 'cost', 'saleprice'],
  description: ['item', 'itemdescription', 'desc', 'particulars', 'itemname'],
  sku: ['sku', 'productcode', 'itemcode', 'partnumber', 'modelnumber'],
  discount: ['discount', 'rebate', 'markdown'],
  tax: ['tax', 'gst', 'vat', 'salestax', 'servicetax'],
  subtotal: ['subtotal', 'netamount', 'netprice'],
  total: ['total', 'grandtotal', 'amountdue', 'balancedue', 'totalamount'],
  currency: ['currency', 'currencycode', 'ccy'],
  invoice_number: ['invoiceno', 'invoicenumber', 'invoiceid', 'billno', 'billnumber', 'refno', 'referencenumber'],
  po_number: ['ponumber', 'purchaseorder', 'ordernumber', 'orderid', 'orderno'],
  invoice_date: ['date', 'invoicedate', 'orderdate', 'billdate'],
  due_date: ['duedate', 'paymentdue', 'paybydate'],
  payment_terms: ['terms', 'paymentterms'],
  tracking_number: ['tracking', 'trackingid', 'trackingno', 'awb', 'awbno', 'consignmentno', 'waybill'],
  carrier: ['carrier', 'courier', 'shippingline', 'logisticspartner'],
  weight: ['weight', 'wt', 'grossweight', 'netweight'],
  notes: ['notes', 'remarks', 'comments', 'memo'],
  website: ['website', 'url', 'site', 'weblink'],
  category: ['category', 'type', 'productcategory'],
};

const DEFAULT_CONFLICT_GROUPS: Record<string, string[]> = {
  qty: ['service', 'services', 'tax', 'gst', 'vat', 'total', 'subtotal', 'discount', 'balance', 'grandtotal', 'shipping', 'fee'],
  rate: ['service', 'services', 'tax', 'gst', 'vat', 'total', 'subtotal', 'discount', 'balance', 'grandtotal', 'shipping', 'fee'],
  tax: ['rate', 'price', 'qty', 'quantity'],
  discount: ['rate', 'price'],
};

function mergeConflictGroups(overrides?: Record<string, string[]>): Record<string, string[]> {
  if (!overrides) return DEFAULT_CONFLICT_GROUPS;
  const merged: Record<string, string[]> = {};
  const keys = new Set([...Object.keys(DEFAULT_CONFLICT_GROUPS), ...Object.keys(overrides)]);
  for (const k of keys) merged[k] = [...(DEFAULT_CONFLICT_GROUPS[k] || []), ...(overrides[k] || [])];
  return merged;
}

export function autoMapColumns(
  headers: string[],
  fields: FieldToMap[],
  options: AutoMapOptions = {}
): Record<string, string | null> {
  const threshold = (options.threshold ?? 0.72) * 100;
  const conflictGroups = mergeConflictGroups(options.conflictGroups);

  interface Pair { fieldKey: string; header: string; score: number; candidateLen: number; headerLen: number }
  const pairs: Pair[] = [];

  for (const field of fields) {
    const aliasList = [...(ALIASES[field.key] || []), ...(options.extraAliases?.[field.key] || [])];
    const candidates = [field.key, field.label, ...aliasList];
    const blocked = new Set((conflictGroups[field.key] || []).map(normalize));

    for (const header of headers) {
      const normHeader = normalize(header);
      const isDirectAlias = aliasList.some(a => normalize(a) === normHeader) || normalize(field.key) === normHeader;
      if (!isDirectAlias && blocked.has(normHeader)) continue;

      let localBest = isDirectAlias ? 100 : 0;
      let bestCandidateLen = isDirectAlias ? normalize(field.key).length : 0;
      for (const candidate of candidates) {
        const score = fuzz.token_set_ratio(candidate, header, { full_process: true });
        if (score > localBest) {
          localBest = score;
          bestCandidateLen = normalize(candidate).length;
        }
      }
      if (localBest >= threshold) {
        pairs.push({ fieldKey: field.key, header, score: localBest, candidateLen: bestCandidateLen, headerLen: normHeader.length });
      }
    }
  }

  pairs.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const aDiff = Math.abs(a.headerLen - a.candidateLen);
    const bDiff = Math.abs(b.headerLen - b.candidateLen);
    return aDiff - bDiff; // closer length match wins the tie
  });

  const result: Record<string, string | null> = {};
  fields.forEach(f => { result[f.key] = null; });
  const usedHeaders = new Set<string>();
  const usedFields = new Set<string>();

  for (const pair of pairs) {
    if (usedHeaders.has(pair.header) || usedFields.has(pair.fieldKey)) continue;
    result[pair.fieldKey] = pair.header;
    usedHeaders.add(pair.header);
    usedFields.add(pair.fieldKey);
  }

  return result;
}
  

